import React, { useEffect, useRef, useState } from 'react';
import ScoreRadioGroup from '../ui/ScoreRadioGroup';
import type {
  EPRItem, Score1to5, CompetencyDef, ScaleDef, EvidenceTriggerConfig,
} from '../../types/eprFramework';
import { CORE_COMPETENCIES, LEADERSHIP_SKILLS } from '../../types/eprFramework';
import {
  scoreTriggersEvidence, isScore1to5,
  computeAchievementPct, suggestScoreFromAchievement,
} from '../../services/eprScoreEngine';
import type { ValidationIssue } from '../../services/eprScoreEngine';

/**
 * EPRItemCard — single item row in an EPR form.
 *
 * Renders score picker for the current scorer (employee or manager) and
 * conditionally reveals the evidence textarea when the score triggers
 * §C.7 (score = 5 or ≤ 2). For OKR/KPI items, also shows target/actual/%.
 *
 * Manager mode shows the employee's self-score as a small badge for context.
 */

export type ScorerSide = 'employee' | 'manager';

const COMPONENT_LEGENDS: Record<EPRItem['component'], Readonly<Record<number, string>>> = {
  okr: {
    1: 'Little / none',
    2: 'Some progress',
    3: 'Met',
    4: 'Exceeded',
    5: 'Far exceeded',
  },
  kpi: {
    1: 'Unsatisfactory',
    2: 'Needs improvement',
    3: 'Meet',
    4: 'Exceeded',
    5: 'Exceptional',
  },
  core: {
    1: 'Below standard',
    2: 'Needs improvement',
    3: 'Meets expectation',
    4: 'Strong',
    5: 'Role model',
  },
  leadership: {
    1: 'Below standard',
    2: 'Needs improvement',
    3: 'Meets expectation',
    4: 'Strong',
    5: 'Role model',
  },
};

function rubricFor(item: EPRItem, defs?: CompetencyDef[]): string | undefined {
  if (item.component === 'core' || item.component === 'leadership') {
    const list = defs
      ?? (item.component === 'core' ? CORE_COMPETENCIES : LEADERSHIP_SKILLS);
    return list.find(c => c.key === item.itemId)?.description ?? item.description;
  }
  return item.description;
}

function rubricRowForScore(item: EPRItem, score: Score1to5, defs?: CompetencyDef[]): string | undefined {
  if (item.component !== 'core' && item.component !== 'leadership') return undefined;
  const list = defs
    ?? (item.component === 'core' ? CORE_COMPETENCIES : LEADERSHIP_SKILLS);
  return list.find(c => c.key === item.itemId)?.rubric[score];
}

export interface EPRItemCardProps {
  item: EPRItem;
  scorer: ScorerSide;
  disabled?: boolean;
  issues?: ValidationIssue[];
  /**
   * Competency list from the submission's templateSnapshot. Fallback:
   * the §C constants — submissions launched before snapshots existed.
   */
  competencyDefs?: CompetencyDef[];
  /**
   * Band table from the snapshot (kpi or okr) — enables the suggested
   * score hint once Achievement % is known.
   */
  scaleDef?: ScaleDef;
  /** Evidence trigger from the snapshot's scaleConfig (default §C.7). */
  evidenceTrigger?: EvidenceTriggerConfig;
  onScoreChange: (score: Score1to5) => void;
  onEvidenceChange: (value: string) => void;
  /** OKR/KPI only — caller may omit for core/leadership. */
  onTargetChange?: (value: string) => void;
  onActualChange?: (value: string) => void;
  onAchievementChange?: (value: number | undefined) => void;
  /** Employee-added items only — renders a remove button while draft. */
  onRemove?: () => void;
}

const EPRItemCard: React.FC<EPRItemCardProps> = ({
  item,
  scorer,
  disabled = false,
  issues = [],
  competencyDefs,
  scaleDef,
  evidenceTrigger,
  onScoreChange,
  onEvidenceChange,
  onTargetChange,
  onActualChange,
  onAchievementChange,
  onRemove,
}) => {
  const currentScore = scorer === 'employee' ? item.employeeSelfScore : item.managerScore;
  const otherScore   = scorer === 'employee' ? item.managerScore       : item.employeeSelfScore;

  const evidenceRequired = currentScore != null && scoreTriggersEvidence(currentScore, evidenceTrigger);
  const evidenceMissing = evidenceRequired && !(item.notesEvidence?.trim());

  const showTargetActual = item.component === 'okr' || item.component === 'kpi';
  const canEditTargetActual = scorer === 'employee' && showTargetActual && !disabled;

  // Local state for free-text inputs (debounced commit to parent)
  const [evidenceLocal, setEvidenceLocal] = useState(item.notesEvidence ?? '');
  const [targetLocal,   setTargetLocal]   = useState(item.target ?? '');
  const [actualLocal,   setActualLocal]   = useState(item.actual ?? '');
  const [achLocal,      setAchLocal]      = useState<string>(
    item.achievementPct != null ? String(item.achievementPct) : '',
  );

  // Keep local in sync if the source item is replaced (e.g. server refresh)
  useEffect(() => { setEvidenceLocal(item.notesEvidence ?? ''); }, [item.notesEvidence]);
  useEffect(() => { setTargetLocal(item.target ?? ''); }, [item.target]);
  useEffect(() => { setActualLocal(item.actual ?? ''); }, [item.actual]);
  useEffect(() => {
    setAchLocal(item.achievementPct != null ? String(item.achievementPct) : '');
  }, [item.achievementPct]);

  // Debounce timers
  const evRef     = useRef<number | null>(null);
  const targetRef = useRef<number | null>(null);
  const actualRef = useRef<number | null>(null);
  const achRef    = useRef<number | null>(null);

  const debounce = (
    ref: React.MutableRefObject<number | null>,
    fn: () => void,
    ms = 800,
  ) => {
    if (ref.current) window.clearTimeout(ref.current);
    ref.current = window.setTimeout(fn, ms);
  };

  const itemIssues = issues.filter(i => i.itemId === item.itemId);
  const evIssue    = itemIssues.find(i => i.field === 'notesEvidence');
  const scoreIssue = itemIssues.find(i => i.field === 'employeeSelfScore' || i.field === 'managerScore');

  // Suggested score per the band table (§C.3 / §C.4) once Achievement %
  // is known. A hint with one-tap apply — never auto-set; the scorer can
  // always pick a different score.
  const achForSuggest = achLocal.trim() !== '' && Number.isFinite(Number(achLocal))
    ? Number(achLocal)
    : item.achievementPct;
  const suggestedScore = scaleDef != null && achForSuggest != null
    ? suggestScoreFromAchievement(achForSuggest, scaleDef)
    : undefined;
  const suggestedBand = suggestedScore != null
    ? scaleDef?.bands.find(b => b.score === suggestedScore)
    : undefined;
  const showSuggestion = !disabled
    && suggestedScore != null
    && suggestedScore !== currentScore;

  // When Target and Actual are both numeric, Achievement % follows
  // automatically — the manual field stays for non-numeric targets.
  const autoFillAchievement = (target: string, actual: string) => {
    const pct = computeAchievementPct(target, actual);
    if (pct != null) {
      setAchLocal(String(pct));
      debounce(achRef, () => onAchievementChange?.(pct));
    }
  };

  return (
    <div
      className={[
        'rounded-xl border bg-white p-5',
        scoreIssue || evIssue ? 'border-red-300' : 'border-stone-200',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-base font-semibold text-stone-900">{item.itemName}</div>
          {rubricFor(item, competencyDefs) && (
            <div className="text-sm text-stone-500 leading-snug mt-0.5">{rubricFor(item, competencyDefs)}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {scorer === 'manager' && otherScore != null && (
            <div
              className="text-xs px-2 py-1 rounded-md bg-stone-100 text-stone-700 border border-stone-200"
              aria-label={`Employee self-score: ${otherScore}`}
            >
              Self: <span className="font-semibold">{otherScore}</span>
            </div>
          )}
          {onRemove && !disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="text-stone-400 hover:text-red-600 transition-colors"
              aria-label={`Remove "${item.itemName}"`}
              title="Remove this item"
            >
              <span className="material-symbols-rounded" style={{ fontSize: 20 }} aria-hidden="true">
                delete
              </span>
            </button>
          )}
        </div>
      </div>

      {showTargetActual && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <FieldText
            label="Target"
            value={targetLocal}
            disabled={!canEditTargetActual}
            onChange={v => {
              setTargetLocal(v);
              debounce(targetRef, () => onTargetChange?.(v));
              autoFillAchievement(v, actualLocal);
            }}
            placeholder="—"
          />
          <FieldText
            label="Actual"
            value={actualLocal}
            disabled={!canEditTargetActual}
            onChange={v => {
              setActualLocal(v);
              debounce(actualRef, () => onActualChange?.(v));
              autoFillAchievement(targetLocal, v);
            }}
            placeholder="—"
          />
          <FieldText
            label="Achievement %"
            value={achLocal}
            disabled={!canEditTargetActual}
            inputMode="numeric"
            onChange={v => {
              setAchLocal(v);
              debounce(achRef, () => {
                const n = v.trim() === '' ? undefined : Number(v);
                onAchievementChange?.(Number.isFinite(n) ? n : undefined);
              });
            }}
            placeholder="—"
          />
        </div>
      )}

      {showSuggestion && suggestedScore != null && (
        <div className="mb-3 flex items-center gap-2 flex-wrap rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
          <span className="text-sm text-blue-900">
            Suggested score for {achForSuggest}% achievement:{' '}
            <span className="font-semibold">{suggestedScore}</span>
            {suggestedBand && ` — ${suggestedBand.label}`}
          </span>
          <button
            type="button"
            onClick={() => isScore1to5(suggestedScore) && onScoreChange(suggestedScore)}
            className="text-sm font-medium text-blue-800 underline underline-offset-2 hover:text-blue-900"
          >
            Use {suggestedScore}
          </button>
        </div>
      )}

      <ScoreRadioGroup
        label={scorer === 'employee' ? 'Self-Score' : 'Manager Score'}
        description={
          currentScore != null && isScore1to5(currentScore)
            ? rubricRowForScore(item, currentScore, competencyDefs) ?? undefined
            : undefined
        }
        value={currentScore}
        onChange={s => onScoreChange(s as Score1to5)}
        legend={COMPONENT_LEGENDS[item.component]}
        disabled={disabled}
      />

      {scoreIssue && (
        <div className="mt-2 text-sm text-red-700" role="alert">
          {scoreIssue.message}
        </div>
      )}

      {evidenceRequired && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-stone-800 mb-1">
            Notes / Evidence
            <span className="ml-1 text-red-600" aria-hidden="true">*</span>
            <span className="ml-2 text-xs font-normal text-stone-500">
              Required for score {currentScore}
            </span>
          </label>
          <textarea
            value={evidenceLocal}
            disabled={disabled}
            onChange={e => {
              const v = e.target.value;
              setEvidenceLocal(v);
              debounce(evRef, () => onEvidenceChange(v));
            }}
            rows={3}
            className={[
              'w-full rounded-xl border bg-white px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-stone-400',
              evidenceMissing ? 'border-red-300' : 'border-stone-300',
            ].join(' ')}
            placeholder="Berikan contoh / bukti spesifik..."
          />
          {evIssue && (
            <div className="mt-1 text-sm text-red-700" role="alert">
              {evIssue.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface FieldTextProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}

const FieldText: React.FC<FieldTextProps> = ({
  label, value, onChange, disabled, placeholder, inputMode,
}) => (
  <div>
    <label className="block text-xs font-medium text-stone-600 mb-1">{label}</label>
    <input
      type="text"
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      className={[
        'w-full rounded-lg border bg-white px-3 py-2 text-sm',
        'focus:outline-none focus:ring-2 focus:ring-stone-400',
        'disabled:bg-stone-50 disabled:text-stone-600',
        'border-stone-300',
      ].join(' ')}
    />
  </div>
);

export default EPRItemCard;
